import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, LogOut, Phone, Mail, Car, Calendar, Clock, User, RefreshCw,
  CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'mmw_admin_token';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  { value: 'quoted', label: 'Quoted', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { value: 'completed', label: 'Completed', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500/20 text-red-400 border-red-500/40' }
];

const getStatusBadge = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return iso; }
};

const formatSlotDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  } catch { return dateStr; }
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hr12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hr12}:${m} ${period}`;
};

// =====================
// Login View
// =====================
const LoginView = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }
      if (!res.ok) throw new Error(data.detail || 'Invalid credentials');
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (e) {
      setError(e.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-zinc-700">
            <Sparkles className="w-8 h-8 text-zinc-300" />
          </div>
          <h1 className="text-3xl font-bold">Mad Max Wax</h1>
          <p className="text-zinc-400 text-sm mt-1">Admin Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 space-y-5">
          <div>
            <Label htmlFor="username" className="text-zinc-300 mb-2 block">Username</Label>
            <Input
              id="username"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-zinc-300 mb-2 block">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          {error && (
            <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-white text-black hover:bg-zinc-200 font-semibold"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// =====================
// Booking Card
// =====================
const BookingCard = ({ booking, token, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    status: booking.status,
    finalQuote: booking.finalQuote || '',
    adminNotes: booking.adminNotes || '',
    confirmedSlotIdx: booking.confirmedSlot
      ? booking.preferredSlots.findIndex(s => s.date === booking.confirmedSlot.date && s.time === booking.confirmedSlot.time)
      : -1,
    suggestedDate: booking.suggestedSlot?.date || '',
    suggestedTime: booking.suggestedSlot?.time || ''
  });

  const vehicleDisplay = booking.vehicleType === 'Other' && booking.customVehicleType
    ? booking.customVehicleType
    : booking.vehicleType;

  const badge = getStatusBadge(booking.status);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status: editData.status,
        finalQuote: editData.finalQuote,
        adminNotes: editData.adminNotes
      };
      if (editData.confirmedSlotIdx >= 0 && booking.preferredSlots[editData.confirmedSlotIdx]) {
        payload.confirmedSlot = booking.preferredSlots[editData.confirmedSlotIdx];
      }
      if (editData.suggestedDate && editData.suggestedTime) {
        payload.suggestedSlot = { date: editData.suggestedDate, time: editData.suggestedTime };
      }
      const res = await fetch(`${API}/api/admin/bookings/${booking.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      onUpdate(data);
      setEditing(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try {
      const res = await fetch(`${API}/api/admin/bookings/${booking.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      onDelete(booking.id);
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div data-testid={`booking-card-${booking.id}`} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
      {/* Header / Summary */}
      <div className="p-5 cursor-pointer hover:bg-zinc-800/50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-white">
                {booking.firstName} {booking.lastName}
              </h3>
              <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border ${badge.color}`}>
                {badge.label}
              </span>
            </div>
            <div className="text-sm text-zinc-400 space-y-1">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5" />{booking.vehicleYear} {booking.vehicleMake} {booking.vehicleModel} <span className="text-zinc-600">({vehicleDisplay})</span></span>
                <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{booking.phone}</span>
              </div>
              <p className="text-xs text-zinc-500">Submitted {formatDate(booking.createdAt)}</p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            {booking.estimatedTotal && (
              <p className="text-white font-bold">{booking.estimatedTotal}</p>
            )}
            <button className="text-zinc-400 hover:text-white mt-2">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-zinc-800 p-5 space-y-5 bg-black/40">
          {/* Contact */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <a href={`tel:${booking.phone}`} className="text-white hover:underline flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-500" />{booking.phone}</a>
              {booking.email && (
                <a href={`mailto:${booking.email}`} className="text-white hover:underline flex items-center gap-2"><Mail className="w-4 h-4 text-zinc-500" />{booking.email}</a>
              )}
            </div>
          </section>

          {/* Time slots */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Preferred Times</h4>
            <div className="space-y-1.5">
              {booking.preferredSlots.map((slot, idx) => {
                const isConfirmed = booking.confirmedSlot && booking.confirmedSlot.date === slot.date && booking.confirmedSlot.time === slot.time;
                return (
                  <div key={idx} className={`flex items-center gap-2 text-sm ${isConfirmed ? 'text-green-400 font-semibold' : 'text-white'}`}>
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatSlotDate(slot.date)} at {formatTime(slot.time)}</span>
                    {isConfirmed && <span className="text-xs uppercase tracking-wider bg-green-500/20 border border-green-500/40 px-2 py-0.5 rounded-full">Confirmed</span>}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Services */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Services</h4>
            <div className="space-y-1">
              {Object.entries(booking.tierSelections || {}).map(([category, tier]) => (
                <div key={category} className="text-sm text-white">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 inline mr-1.5" />
                  {category} — <span className="uppercase text-green-400 font-semibold">{tier}</span>
                </div>
              ))}
              {(booking.selectedAddOns || []).map((addon, i) => (
                <div key={i} className="text-sm text-white">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 inline mr-1.5" />
                  {addon}
                </div>
              ))}
              {booking.otherService && (
                <div className="text-sm text-zinc-300 italic mt-2 pt-2 border-t border-zinc-800">
                  <span className="text-zinc-500 text-xs">Custom request: </span>{booking.otherService}
                </div>
              )}
            </div>
          </section>

          {booking.estimatedTotal && (
            <section>
              <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Customer Estimated Total</h4>
              <p className="text-lg font-bold text-white">{booking.estimatedTotal}</p>
            </section>
          )}

          {/* Edit / Quote section */}
          {editing ? (
            <section className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-white">Update Booking</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs mb-1 block">Status</Label>
                  <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1 block">Final Quote</Label>
                  <Input
                    value={editData.finalQuote}
                    onChange={(e) => setEditData({...editData, finalQuote: e.target.value})}
                    placeholder="e.g., $375"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1 block">Confirm a Time Slot</Label>
                <Select
                  value={String(editData.confirmedSlotIdx)}
                  onValueChange={(v) => setEditData({...editData, confirmedSlotIdx: parseInt(v, 10)})}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="None confirmed" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                    <SelectItem value="-1">None confirmed</SelectItem>
                    {booking.preferredSlots.map((s, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {formatSlotDate(s.date)} at {formatTime(s.time)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 space-y-3">
                <div>
                  <Label className="text-zinc-400 text-xs mb-1 block">Suggest Alternative Time (if neither preferred works)</Label>
                  <p className="text-xs text-zinc-500 mb-2">Use this when both customer preferences don't work for you. You can call/text the customer this suggestion.</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={editData.suggestedDate}
                    onChange={(e) => setEditData({...editData, suggestedDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Input
                    type="time"
                    step="3600"
                    value={editData.suggestedTime}
                    onChange={(e) => setEditData({...editData, suggestedTime: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                {(editData.suggestedDate || editData.suggestedTime) && (
                  <button
                    type="button"
                    onClick={() => setEditData({...editData, suggestedDate: '', suggestedTime: ''})}
                    className="text-xs text-zinc-500 hover:text-white underline"
                  >
                    Clear suggestion
                  </button>
                )}
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1 block">Admin Notes</Label>
                <Textarea
                  value={editData.adminNotes}
                  onChange={(e) => setEditData({...editData, adminNotes: e.target.value})}
                  placeholder="Private notes about this booking..."
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[70px]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(false)} className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-white text-black hover:bg-zinc-200">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </section>
          ) : (
            <>
              {(booking.finalQuote || booking.adminNotes || booking.suggestedSlot) && (
                <section className="bg-zinc-900/60 border border-zinc-700 rounded-lg p-4 space-y-3">
                  {booking.finalQuote && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Final Quote</p>
                      <p className="text-lg font-bold text-white">{booking.finalQuote}</p>
                    </div>
                  )}
                  {booking.suggestedSlot && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Suggested Alternative Time</p>
                      <p className="text-base font-semibold text-yellow-300">
                        {formatSlotDate(booking.suggestedSlot.date)} at {formatTime(booking.suggestedSlot.time)}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">Contact the customer to confirm this proposed time.</p>
                    </div>
                  )}
                  {booking.adminNotes && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Admin Notes</p>
                      <p className="text-sm text-zinc-200 whitespace-pre-wrap">{booking.adminNotes}</p>
                    </div>
                  )}
                </section>
              )}
              <div className="flex flex-wrap gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  data-testid={`delete-${booking.id}`}
                  className="bg-zinc-900 border-red-900 text-red-400 hover:bg-red-950 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
                <Button
                  onClick={() => setEditing(true)}
                  data-testid={`edit-${booking.id}`}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  Manage Booking
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =====================
// Dashboard
// =====================
const Dashboard = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load bookings');
      setBookings(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleUpdate = (updated) => {
    setBookings(prev => prev.map(b => b.id === updated.id ? updated : b));
  };

  const handleDelete = (id) => {
    setBookings(prev => prev.filter(b => b.id !== id));
  };

  const filtered = filterStatus === 'all'
    ? bookings
    : bookings.filter(b => b.status === filterStatus);

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = bookings.filter(b => b.status === s.value).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center border border-zinc-700">
                <Sparkles className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Mad Max Wax</h1>
                <p className="text-xs text-zinc-400">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={fetchBookings}
                data-testid="refresh-btn"
                className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-btn"
                className="bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <button
            onClick={() => setFilterStatus('all')}
            data-testid="filter-all"
            className={`text-left p-4 rounded-lg border-2 transition-all ${filterStatus === 'all' ? 'bg-white text-black border-white' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'}`}
          >
            <p className="text-xs uppercase tracking-wider opacity-70">All</p>
            <p className="text-2xl font-bold">{bookings.length}</p>
          </button>
          {STATUS_OPTIONS.map(s => (
            <button
              key={s.value}
              onClick={() => setFilterStatus(s.value)}
              data-testid={`filter-${s.value}`}
              className={`text-left p-4 rounded-lg border-2 transition-all ${filterStatus === s.value ? 'bg-white text-black border-white' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'}`}
            >
              <p className="text-xs uppercase tracking-wider opacity-70">{s.label}</p>
              <p className="text-2xl font-bold">{counts[s.value] || 0}</p>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && (
          <div className="text-center py-16 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            <p>Loading bookings...</p>
          </div>
        )}
        {error && (
          <div className="bg-red-950/40 border border-red-900 rounded-lg p-4 text-red-300 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <XCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No bookings in this category yet.</p>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="space-y-4" data-testid="bookings-list">
            {filtered.map(b => (
              <BookingCard
                key={b.id}
                booking={b}
                token={token}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =====================
// Main AdminPage
// =====================
const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [verifying, setVerifying] = useState(!!token);

  // Verify token on mount
  useEffect(() => {
    if (!token) { setVerifying(false); return; }
    fetch(`${API}/api/admin/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const handleLogin = (t) => setToken(t);
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!token) return <LoginView onLogin={handleLogin} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
